// ─── Execution Tracer ─────────────────────────────────────────────────────────
//
// Deterministic execution log using monotonically increasing sequence counters.
// No Date.now() - wall-clock timestamps are non-deterministic and violate
// replay integrity (RDG-1). Each trace entry is identified by sequence number.

import type { SideEffectRecord } from './sideEffectManager';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TraceEntry {
  /** Monotonically increasing. Deterministic across replays given the same action sequence. */
  sequence: number;
  actionId: string;
  actionType: string;
  success: boolean;
  durationMs: number;
  error?: string;
  sideEffects: SideEffectRecord[];
}

// ─── Tracer ───────────────────────────────────────────────────────────────────

class ExecutionTracerImpl {
  private sequence = 0;
  private log: TraceEntry[] = [];

  /**
   * Record a completed action execution. Returns the full entry with assigned sequence.
   */
  record(entry: Omit<TraceEntry, 'sequence'>): TraceEntry {
    const full: TraceEntry = { ...entry, sequence: ++this.sequence };
    this.log.push(full);
    return full;
  }

  /** Return a copy of the full execution log. */
  getLog(): TraceEntry[] {
    return [...this.log];
  }

  /** Return the N most recent entries. */
  getLastN(n: number): TraceEntry[] {
    return this.log.slice(-n);
  }

  /** Return the current sequence counter value (last assigned sequence). */
  currentSequence(): number {
    return this.sequence;
  }

  /** Clear the log and reset the sequence counter. For test isolation and replay reset. */
  clear(): void {
    this.log = [];
    this.sequence = 0;
  }
}

export const executionTracer = new ExecutionTracerImpl();
